'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ComponentProps, ReactNode, Ref } from 'react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { mergeRefs } from '@/src/utils/react'
import { cn } from '@/src/utils/styles'
import { Button } from './button'
import { Back } from './icons/back'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-scrim !pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
)
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends ComponentProps<typeof DialogPrimitive.Content> {
  showBackButton?: boolean
  backButtonProps?: ComponentProps<typeof Button>
  showCloseButton?: boolean
  closeButtonProps?: ComponentProps<typeof Button>
  middle?: ReactNode
  backAction?: () => void
  closeAction?: () => void
  onAfterLeave?: () => void
  headerRef?: Ref<HTMLDivElement>
  headerClassName?: string
  hideHeader?: boolean
  container?: Element | DocumentFragment | null
  scrollWithOverlay?: boolean
  overlayClassName?: string
}

const DialogContent = ({
  className,
  children,
  middle,
  showBackButton,
  showCloseButton,
  backButtonProps,
  closeButtonProps,
  backAction,
  closeAction,
  onAfterLeave,
  headerRef,
  headerClassName,
  hideHeader = false,
  container,
  ref,
  scrollWithOverlay = false,
  overlayClassName,
  ...props
}: DialogContentProps) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [shouldCenterVertically, setShouldCenterVertically] = useState(true)
  const Wrapper = scrollWithOverlay ? 'div' : Fragment
  const externalWrapperProps = scrollWithOverlay
    ? {
        ref: wrapperRef,
        className: cn(
          'rounded-[32px] py-4 w-[320px] sm:w-[448px] lg:w-[unset] absolute z-[99] left-1/2 -translate-x-1/2 md:right-0 max-w-md overflow-hidden',
          {
            'top-1/2 -translate-y-1/2': shouldCenterVertically,
            'top-0': !shouldCenterVertically,
          },
          className,
        ),
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
          // Prevent click events from bubbling up to the dialog content
          e.stopPropagation()
        },
      }
    : {}
  const internalWrapperProps = scrollWithOverlay
    ? {
        ...props,
        className: cn(
          'flex flex-col relative bg-foreground rounded-[32px] overflow-hidden h-full',
          className,
        ),
      }
    : {}

  useEffect(() => {
    const checkContentHeight = () => {
      if (wrapperRef.current) {
        const contentHeight = wrapperRef.current.scrollHeight
        const viewportHeight = window.innerHeight
        setShouldCenterVertically(contentHeight <= viewportHeight)
      }
    }

    // Check height after component mounts and content is rendered
    const timeoutId = setTimeout(checkContentHeight, 0)

    // Also check on window resize
    window.addEventListener('resize', checkContentHeight)

    // Observe content changes
    let observer: MutationObserver | null = null
    if (wrapperRef.current) {
      observer = new MutationObserver(checkContentHeight)
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', checkContentHeight)
      if (observer) {
        observer.disconnect()
      }
    }
  }, [])

  return (
    <DialogPortal container={container} aria-describedby='dialog'>
      <DialogOverlay onClick={closeAction} className={overlayClassName} />
      <DialogPrimitive.Content
        aria-describedby='dialog'
        onAnimationEndCapture={(e) => {
          if (e.animationName === 'exit') onAfterLeave?.()
        }}
        ref={mergeRefs(ref, dialogRef)}
        className={cn(
          'fixed z-50 bg-transparent h-full w-full shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 overflow-hidden overflow-y-auto no-scrollbar',
          {
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] max-h-[min(768px,calc(100vh-32px))] flex flex-col rounded-[32px] bg-foreground top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 max-w-screen-md':
              !scrollWithOverlay,
            'py-8 inset-0': scrollWithOverlay,
          },
          !scrollWithOverlay && className,
        )}
        style={{
          height: scrollWithOverlay ? '100vh !important' : undefined,
          maxWidth: scrollWithOverlay ? 'unset' : undefined,
        }}
        {...(!scrollWithOverlay ? props : {})}
        onClick={(e) => {
          props.onClick?.(e)
          if (scrollWithOverlay) {
            closeAction?.()
          }
        }}
      >
        <Wrapper {...externalWrapperProps}>
          <Wrapper {...internalWrapperProps}>
            {!hideHeader && (
              <DialogHeader
                className={cn(
                  'px-5 sm:px-8 min-h-12 sm:min-h-20 z-[51]',
                  headerClassName,
                  {
                    hidden:
                      showBackButton === false && showCloseButton === false,
                  },
                )}
                ref={headerRef}
              >
                <Button
                  onClick={backAction}
                  type='button'
                  variant='ghost'
                  {...backButtonProps}
                  className={cn(
                    {
                      invisible: !showBackButton,
                    },
                    backButtonProps?.className,
                  )}
                >
                  <Back width={24} height={24} />
                  <span className='sr-only'>Back</span>
                </Button>
                {middle}
                <DialogPrimitive.Close asChild>
                  <Button
                    onClick={closeAction}
                    type='button'
                    variant='ghost'
                    {...closeButtonProps}
                    className={cn(
                      {
                        invisible: !showCloseButton,
                      },
                      closeButtonProps?.className,
                    )}
                  >
                    <X />
                    <span className='sr-only'>Close</span>
                  </Button>
                </DialogPrimitive.Close>
              </DialogHeader>
            )}
            {children}
          </Wrapper>
        </Wrapper>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={cn(
      'flex items-center place-content-between text-center sm:text-left gap-5',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-center text-4xl font-semibold text-primary z-[52]',
      className,
    )}
    {...props}
  />
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-center text-secondary', className)}
    {...props}
  />
)
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogClose = ({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) => {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

DialogClose.displayName = DialogPrimitive.Close.displayName

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
}
